import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { createDefaultOrganizationProfile, ownedOrganizationIds } from '../../util/organizations';
import { usersAPI } from '../../util/api';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  buildSetOrgSubscriptionPatch,
  SUBSCRIPTION_TIERS,
} from '../../util/orgSubscription';
import config from '../../config';

// ================ Action types ================ //

const BRIDGE_UPGRADE_REQUEST = 'app/BridgeSignupPage/BRIDGE_UPGRADE_REQUEST';
const BRIDGE_UPGRADE_SUCCESS = 'app/BridgeSignupPage/BRIDGE_UPGRADE_SUCCESS';
const BRIDGE_UPGRADE_ERROR = 'app/BridgeSignupPage/BRIDGE_UPGRADE_ERROR';

// ================ Reducer ================ //

const initialState = {
  upgradeInProgress: false,
  upgradeError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case BRIDGE_UPGRADE_REQUEST:
      return { ...state, upgradeInProgress: true, upgradeError: null };
    case BRIDGE_UPGRADE_SUCCESS:
      return { ...state, upgradeInProgress: false };
    case BRIDGE_UPGRADE_ERROR:
      return { ...state, upgradeInProgress: false, upgradeError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

const bridgeUpgradeRequest = () => ({ type: BRIDGE_UPGRADE_REQUEST });
const bridgeUpgradeSuccess = () => ({ type: BRIDGE_UPGRADE_SUCCESS });
const bridgeUpgradeError = error => ({ type: BRIDGE_UPGRADE_ERROR, payload: error, error: true });

// ================ Thunks ================ //

/**
 * Upgrade audience user to creator with profile data from bridge form.
 */
export const upgradeToCreator = profileData => async (dispatch, _getState, sdk) => {
  dispatch(bridgeUpgradeRequest());

  try {
    // Prepare location data (same format as CreatorHomePage)
    let locationData = null;
    if (profileData.location) {
      const place = profileData.location.selectedPlace || profileData.location;
      locationData = {
        city: place.address || profileData.location.search || '',
        country: place.origin ? 'Detected' : '',
      };
    }

    const creatorProfileData = {
      artistAlias: profileData.artistAlias || '',
      bio: profileData.bio || '',
      genres: profileData.genres || [],
      location: locationData,
      socialLinks: {
        facebook: profileData.facebookLink || '',
        instagram: profileData.instagramLink || '',
        linkedin: profileData.linkedinLink || '',
      },
      isProfileComplete: true,
    };

    // Include uploaded profile image URL if available
    if (profileData.uploadedImageUrl) {
      creatorProfileData.profileImageUrl = profileData.uploadedImageUrl;
    }

    const updateParams = {
      publicData: {
        role: config.userRoles['creator'],
        creatorProfile: creatorProfileData,
      },
    };

    const response = await sdk.currentUser.updateProfile(updateParams);
    dispatch(addMarketplaceEntities(response));

    dispatch(bridgeUpgradeSuccess());
    await dispatch(fetchCurrentUser());

    return { role: config.userRoles['creator'] };
  } catch (e) {
    dispatch(bridgeUpgradeError(storableError(e)));
    throw e;
  }
};

/**
 * Upgrade audience user to organization with profile data from bridge form.
 */
export const upgradeToOrganization = profileData => async (dispatch, getState, sdk) => {
  dispatch(bridgeUpgradeRequest());

  const state = getState();
  const currentUser = state.user.currentUser;
  const firstName = currentUser?.attributes?.profile?.publicData?.name?.firstName || 'User';
  const userId = currentUser?.id?.uuid;

  try {
    const orgName = profileData.name || `${firstName}'s Organization`;
    const organizationData = createDefaultOrganizationProfile(orgName, firstName);

    // Merge bridge form fields into the org profile (same format as OrgProfileWizardModal)
    const orgProfile = organizationData.publicDataProfiles[0];
    orgProfile.description = profileData.bio || '';
    orgProfile.bio = profileData.bio || '';
    orgProfile.website = profileData.website || '';
    orgProfile.genres = profileData.genres || [];
    orgProfile.founded = profileData.founded || '';
    orgProfile.location = profileData.location || '';
    orgProfile.facebookLink = profileData.facebookLink || '';
    orgProfile.instagramLink = profileData.instagramLink || '';
    orgProfile.linkedinLink = profileData.linkedinLink || '';

    // Include uploaded profile image URL if available
    if (profileData.uploadedImageUrl) {
      orgProfile.profileImage = profileData.uploadedImageUrl;
    }

    const existingProfiles = currentUser?.attributes?.profile?.publicData?.organizationProfiles || [];
    const organizationProfiles = [...existingProfiles, ...organizationData.publicDataProfiles];
    const updateParams = {
      publicData: {
        role: config.userRoles['organization'],
        organizationProfiles,
        organizationProfileIds: ownedOrganizationIds(organizationProfiles),
      },
    };

    const response = await sdk.currentUser.updateProfile(updateParams);
    dispatch(addMarketplaceEntities(response));

    dispatch(bridgeUpgradeSuccess());
    await dispatch(fetchCurrentUser());

    // Get orgId from the created profile
    const updatedUser = getState().user.currentUser;
    const orgProfiles = updatedUser?.attributes?.profile?.publicData?.organizationProfiles || [];
    const orgId = orgProfiles.length > 0 ? orgProfiles[orgProfiles.length - 1].id : null;

    // Save per-org subscription record into privateData (spec shape).
    if (orgId && userId) {
      try {
        const currentPrivateData = updatedUser?.attributes?.profile?.privateData || {};
        const updatedPrivateData = buildSetOrgSubscriptionPatch(
          currentPrivateData,
          orgId,
          {
            tier: SUBSCRIPTION_TIERS.FREEMIUM,
            status: 'active',
          }
        );
        await usersAPI.updateProfile({
          id: userId,
          privateData: updatedPrivateData,
        });
      } catch (subErr) {
        console.warn('Failed to save org subscription during bridge upgrade:', subErr);
      }
    }

    return { role: config.userRoles['organization'], orgId };
  } catch (e) {
    dispatch(bridgeUpgradeError(storableError(e)));
    throw e;
  }
};
